import React, { useEffect, useState } from "react";
import { Alert, Box, ButtonBase, CircularProgress, IconButton, Typography } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { FONT, NEUTRAL, ORANGE, RADIUS, SELECTED_EDGE, VIOLET } from "../../styles/brandTokens";
import { RESOLVER_STATE_COLORS, STATE_DOT_SIZE } from "../../shared/components/resolverStateStyle";
import type { AutoAgentMonitorTicket } from "../../types/integration";
import type { AutoAgentMonitorFilterQuery } from "../theoAutomationsMonitorFilters";
import { activityLabel, OWNER_GROUPS, OwnerGroup, resolverStateText } from "./executionQueueModel";
import useExecutionQueue, { QueueGroupState } from "./useExecutionQueue";

/** Ticket · Theo state · Agent · PSA status · Last activity */
const ROW_COLUMNS = "minmax(240px, 2.6fr) minmax(170px, 1.5fr) minmax(130px, 1fr) minmax(110px, 0.9fr) 104px";
const CLOCK_TICK_MS = 30_000;

const text = (size: number, color: string = NEUTRAL.text, weight = 400) => ({
  fontFamily: FONT.sans,
  fontSize: size,
  color,
  fontWeight: weight,
  lineHeight: 1.4,
});

const StateDot: React.FC<{ row: AutoAgentMonitorTicket }> = ({ row }) => {
  const colors = RESOLVER_STATE_COLORS[row.resolver_state] || RESOLVER_STATE_COLORS.cancelled;
  const isLive = row.resolver_state === "running";
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          width: STATE_DOT_SIZE,
          height: STATE_DOT_SIZE,
          borderRadius: "50%",
          flexShrink: 0,
          backgroundColor: colors.dot,
          ...(isLive && {
            "@keyframes theoLivePulse": { "50%": { opacity: 0.35 } },
            animation: "theoLivePulse 1.6s ease-in-out infinite",
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }),
        }}
      />
      <Box component="span" sx={{ ...text(13, colors.text, 500), whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {resolverStateText(row)}
      </Box>
    </Box>
  );
};

const QueueRow: React.FC<{ row: AutoAgentMonitorTicket; nowMs: number }> = ({ row, nowMs }) => {
  const customer = row.customer_name || row.psa_customer_id;
  return (
    <Box
      component="a"
      href={`/supervisor/tickets/${encodeURIComponent(row.ticket_id)}`}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: "grid",
        gridTemplateColumns: ROW_COLUMNS,
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.1,
        borderTop: `1px solid ${NEUTRAL.border}`,
        textDecoration: "none",
        color: "inherit",
        transition: "background-color 120ms ease",
        "&:hover": { backgroundColor: NEUTRAL.hover },
        "&:hover .queue-title": { color: VIOLET.main },
        "&:focus-visible": { outline: "none", backgroundColor: NEUTRAL.hover, boxShadow: SELECTED_EDGE },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography className="queue-title" noWrap sx={{ ...text(14, NEUTRAL.text, 600), transition: "color 120ms ease" }}>
          {row.ticket_title || `Ticket ${row.ticket_id}`}
        </Typography>
        <Typography noWrap sx={text(12, NEUTRAL.textMuted)}>
          #{row.ticket_id}
          {customer ? ` · ${customer}` : ""}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <StateDot row={row} />
        {row.execution_error ? (
          <Typography noWrap title={row.execution_error} sx={{ ...text(11.5, "#B04646"), pl: 1.6 }}>
            {row.execution_error}
          </Typography>
        ) : null}
      </Box>
      <Typography noWrap sx={text(13, NEUTRAL.textSecondary)}>
        {row.agent_name || "—"}
      </Typography>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={text(13, NEUTRAL.textSecondary)}>
          {row.psa_status_name || "Unknown"}
        </Typography>
        {row.status_refresh_error ? <Typography sx={text(11.5, ORANGE.text)}>Couldn't refresh</Typography> : null}
      </Box>
      <Typography noWrap sx={{ ...text(12.5, NEUTRAL.textMuted), textAlign: "right" }}>
        {activityLabel(row, nowMs)}
      </Typography>
    </Box>
  );
};

const GroupPager: React.FC<{ group: OwnerGroup; state: QueueGroupState; onPage: (page: number) => void }> = ({
  group,
  state,
  onPage,
}) => {
  if (state.totalCount <= group.pageSize) return null;
  const first = state.page * group.pageSize + 1;
  const last = Math.min(state.totalCount, first + group.pageSize - 1);
  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5, px: 1.5, py: 0.5, borderTop: `1px solid ${NEUTRAL.border}` }}>
      <Typography sx={text(12, NEUTRAL.textMuted)}>
        {first}–{last} of {state.totalCount}
      </Typography>
      <IconButton size="small" aria-label={`Previous ${group.label} page`} disabled={state.page === 0} onClick={() => onPage(state.page - 1)}>
        <ChevronLeftRoundedIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" aria-label={`Next ${group.label} page`} disabled={last >= state.totalCount} onClick={() => onPage(state.page + 1)}>
        <ChevronRightRoundedIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

const EMPTY_TEXT: Record<OwnerGroup["key"], string> = {
  needs_you: "Nothing is waiting on a technician.",
  working: "Theo isn't running anything right now.",
  done_open: "No finished tickets left open.",
  closed: "No closed tickets yet.",
};

const QueueGroup: React.FC<{
  group: OwnerGroup;
  state: QueueGroupState;
  count: number;
  isExpanded: boolean;
  nowMs: number;
  onToggle: () => void;
  onPage: (page: number) => void;
}> = ({ group, state, count, isExpanded, nowMs, onToggle, onPage }) => {
  const labelColor = group.attention && count > 0 ? ORANGE.text : NEUTRAL.textSecondary;
  const headingId = `queue-group-${group.key}`;
  return (
    <Box component="section" aria-labelledby={headingId}>
      <ButtonBase
        onClick={onToggle}
        aria-expanded={isExpanded}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          gap: 1,
          px: 2,
          pt: 1.75,
          pb: 1,
          backgroundColor: NEUTRAL.subtle,
          borderTop: `1px solid ${NEUTRAL.border}`,
          "&:hover .queue-chevron": { color: NEUTRAL.text },
          "&.Mui-focusVisible": { boxShadow: SELECTED_EDGE },
        }}
      >
        <ExpandMoreRoundedIcon
          className="queue-chevron"
          fontSize="small"
          sx={{ color: NEUTRAL.textMuted, transform: isExpanded ? "none" : "rotate(-90deg)", transition: "transform 150ms ease" }}
        />
        <Typography id={headingId} component="h3" sx={{ ...text(12, labelColor, 700), letterSpacing: "0.04em", textTransform: "uppercase" }}>
          {group.label} · {count}
        </Typography>
        {group.hint && count > 0 ? <Typography sx={text(12, NEUTRAL.textMuted)}>{group.hint}</Typography> : null}
        {state.loading && state.rows.length > 0 ? <CircularProgress size={12} sx={{ color: VIOLET.main, ml: 0.5 }} /> : null}
      </ButtonBase>
      {isExpanded ? (
        <>
          {state.error ? (
            <Box sx={{ px: 2, py: 1 }}>
              <Alert severity="error">{state.error}</Alert>
            </Box>
          ) : null}
          {state.loading && state.rows.length === 0 ? (
            <Box sx={{ px: 2, py: 2, borderTop: `1px solid ${NEUTRAL.border}` }}>
              <CircularProgress size={18} sx={{ color: VIOLET.main }} />
            </Box>
          ) : state.rows.length === 0 && !state.error ? (
            <Typography sx={{ ...text(13, NEUTRAL.textMuted), px: 2, py: 1.25, borderTop: `1px solid ${NEUTRAL.border}` }}>
              {EMPTY_TEXT[group.key]}
            </Typography>
          ) : (
            state.rows.map((row) => <QueueRow key={row.id} row={row} nowMs={nowMs} />)
          )}
          <GroupPager group={group} state={state} onPage={onPage} />
        </>
      ) : null}
    </Box>
  );
};

/** Re-renders relative times ("running 2m") between polls without refetching. */
function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);
  return now;
}

const ExecutionQueue: React.FC<{
  agentId: number;
  filterQuery: AutoAgentMonitorFilterQuery;
  filterKey: string;
  refreshToken: number;
}> = ({ agentId, filterQuery, filterKey, refreshToken }) => {
  const { groups, counts, expanded, liveError, toggleGroup, setPage } = useExecutionQueue({
    agentId,
    filterQuery,
    filterKey,
    refreshToken,
  });
  const nowMs = useNow(CLOCK_TICK_MS);

  return (
    <Box sx={{ background: NEUTRAL.surface, border: `1px solid ${NEUTRAL.border}`, borderRadius: `${RADIUS.card}px`, overflow: "hidden" }}>
      {liveError ? (
        <Typography role="status" sx={{ ...text(12, ORANGE.text), px: 2, py: 0.75, backgroundColor: ORANGE.wash }}>
          Live updates paused: {liveError}. Retrying.
        </Typography>
      ) : null}
      <Box sx={{ overflowX: "auto" }}>
        <Box sx={{ minWidth: 820 }}>
          <Box
            role="presentation"
            sx={{
              display: "grid",
              gridTemplateColumns: ROW_COLUMNS,
              gap: 2,
              px: 2,
              py: 1,
              ...text(11.5, NEUTRAL.textMuted, 600),
            }}
          >
            <span>Ticket</span>
            <span>Theo</span>
            <span>Agent</span>
            <span>PSA status</span>
            <span style={{ textAlign: "right" }}>Last activity</span>
          </Box>
          {OWNER_GROUPS.map((group) => (
            <QueueGroup
              key={group.key}
              group={group}
              state={groups[group.key]}
              count={counts[group.key]}
              isExpanded={expanded[group.key]}
              nowMs={nowMs}
              onToggle={() => toggleGroup(group.key)}
              onPage={(page) => setPage(group.key, page)}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ExecutionQueue;
